import { Check, Copy, Link2, Plus, Power } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { type ShareLinkRow, useCreateShare, useShares, useUpdateShare } from "@/hooks/use-shares";

function shareUrl(token: string): string {
  return `${window.location.origin}/share/${token}`;
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function CopyButton({ token }: { token: string }) {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    const ok = await copyToClipboard(shareUrl(token));
    if (!ok) {
      toast.error("Could not copy");
      return;
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Button variant="ghost" size="sm" onClick={onClick}>
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  );
}

function ShareRow({ slug, link }: { slug: string; link: ShareLinkRow }) {
  const update = useUpdateShare(slug);
  return (
    <div
      className={`rounded-md border px-3 py-3 space-y-2 ${
        link.isActive ? "bg-card" : "bg-muted/40 opacity-70"
      }`}
    >
      <div className="flex items-center gap-2">
        <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
        <code className="text-xs font-mono truncate flex-1" title={shareUrl(link.token)}>
          {shareUrl(link.token)}
        </code>
        <CopyButton token={link.token} />
        <Button
          variant="ghost"
          size="sm"
          title={link.isActive ? "Deactivate" : "Reactivate"}
          onClick={() => update.mutate({ shareId: link.id, isActive: !link.isActive })}
        >
          <Power
            className={`h-3.5 w-3.5 ${link.isActive ? "text-emerald-600" : "text-muted-foreground"}`}
          />
        </Button>
      </div>
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={link.hideConcerns}
            onChange={(e) => update.mutate({ shareId: link.id, hideConcerns: e.target.checked })}
          />
          Hide concerns
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={link.hideComments}
            onChange={(e) => update.mutate({ shareId: link.id, hideComments: e.target.checked })}
          />
          Hide comments
        </label>
        <span className="ml-auto">{new Date(link.createdAt).toLocaleDateString()}</span>
      </div>
    </div>
  );
}

export function ShareLinksDialog({
  slug,
  open,
  onOpenChange,
}: {
  slug: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const shares = useShares(slug);
  const create = useCreateShare(slug);
  const [hideConcerns, setHideConcerns] = useState(true);
  const [hideComments, setHideComments] = useState(true);

  const onCreate = async () => {
    const link = await create.mutateAsync({ hideConcerns, hideComments });
    if (link?.token) {
      const copied = await copyToClipboard(shareUrl(link.token));
      toast.success(copied ? "Share link created and copied" : "Share link created");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Share this solution</DialogTitle>
          <DialogDescription>
            Generate read-only links that anyone with the URL can open — no login required.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border p-3 space-y-2 bg-muted/30">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            New link
          </div>
          <div className="flex items-center gap-4 text-sm">
            <Label className="flex items-center gap-1.5 cursor-pointer text-xs">
              <input
                type="checkbox"
                checked={hideConcerns}
                onChange={(e) => setHideConcerns(e.target.checked)}
              />
              Hide internal concerns
            </Label>
            <Label className="flex items-center gap-1.5 cursor-pointer text-xs">
              <input
                type="checkbox"
                checked={hideComments}
                onChange={(e) => setHideComments(e.target.checked)}
              />
              Hide comments
            </Label>
            <Button size="sm" className="ml-auto" onClick={onCreate} disabled={create.isPending}>
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Generate link
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            The link follows the current version. Set a specific version after creating if you need
            it pinned.
          </p>
        </div>

        <div className="space-y-2 max-h-72 overflow-y-auto">
          {shares.isLoading && <Skeleton className="h-16" />}
          {shares.isError && !shares.isLoading && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-4 text-center">
              <p className="text-xs text-destructive mb-2">Could not load share links.</p>
              <Button variant="outline" size="sm" onClick={() => shares.refetch()}>
                Try again
              </Button>
            </div>
          )}
          {!shares.isLoading && !shares.isError && shares.data?.length === 0 && (
            <div className="text-xs text-muted-foreground text-center py-6">
              No share links yet.
            </div>
          )}
          {shares.data?.map((s) => (
            <ShareRow key={s.id} slug={slug} link={s} />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
